import {
  all,
  call,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from '@redux-saga/core/effects';
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import {
  COMMENTSTATUS_AWAITING_APPROVAL,
  COMMENTSTATUS_NONE,
  COMMENTSTATUS_NOTAPPROVED,
  COMMENTTYPE_HEARING_RESPONSE,
  COMMENTTYPE_HEARING_RESPONSE_REPLY,
  COMMENTTYPE_HEARING_REVIEW,
  CONTENTTYPE_FILE,
  CONTENTTYPE_TEXT,
  CONTENT_TYPE,
  FILEOPERATION_ADD,
  FILEOPERATION_DELETE,
  HEARINGSTATUS_ACTIVE,
  HEARINGSTATUS_AWAITING_CONCLUSION,
  HEARINGSTATUS_AWAITING_STARTDATE,
  HEARINGSTATUS_DRAFT,
} from '../../constants/api';
import { COMPONENT_REDUCER } from '../../constants/store';
import { convertDataUrlToFile } from '../../utilities/files';
import {
  mapFileContentDiff,
  mapFileContentWithUrlToDownload,
} from '../../utilities/mapperFunctions';
import {
  createCommentContentInitialize,
  getAllCommentsForHearingError,
  getAllCommentsForHearingInitialize,
  getAllCommentsForHearingSuccess,
  getPaginatedCommentsForHearingError,
  getPaginatedCommentsForHearingInitialize,
  getPaginatedCommentsForHearingSuccess,
  removeContentsFromStore,
  updateCommentContentInitialize,
  updateCommentInitialize,
} from '../actions/api';
import { allNotDeletedCommentsWithTypeByHearingIdSelector } from '../apiFeatures/commentSlice';
import {
  commentTypeByCommentIdSelector,
  commentTypeByTypeSelector,
} from '../apiFeatures/commentTypeSlice';
import { allCompaniesSelector } from '../apiFeatures/companySlice';
import { contentByHearingIdSelector } from '../apiFeatures/contentSlice';
import { contentTypeByTypeSelector } from '../apiFeatures/contentTypeSlice';
import { allUsersSelector } from '../apiFeatures/userSlice';
import {
  baseApiUrlSelector,
  shouldPaginateCommentsSelector,
} from '../bootstrapSlice';
import {
  globalMessageModes,
  parseApiError,
  setGlobalMessage,
} from '../globalMessageSlice';
import { getAttr, getEntityId, getRelId } from '../helpers/apiHelpers';
import { awaitApiCall, callAndAwaitApi } from '../helpers/sagaHelpers';
import {
  getInitActionPayload,
  getRequestData,
  getRequestMetaData,
} from '../helpers/selectorHelpers';
import { buildIncludesString } from '../../utilities/utilityFunctions';
import { commentIncludes } from '../../constants/includes';

// ---------- ACTIONS -----------
const setIsUpdatingComment = createAction(
  'component/commentSection/setIsUpdatingComment',
);
const updateContentDiff = createAction(
  'component/commentSection/updateContentDiff',
);
const updateContentDiffInternal = createAction(
  'component/commentSection/updateContentDiff/internal',
);
const resetContentDiff = createAction(
  'component/commentSection/resetContentDiff',
);
const createOrUpdateCommentContentInitialize = createAction(
  'component/commentSection/createOrUpdateCommentInitialize',
);
const createOrUpdateCommentReplyInitialize = createAction(
  'component/commentSection/createOrUpdateCommentReplyInitialize',
);
const updateCommentStatusInitialize = createAction(
  'component/commentSection/updateCommentStatusInitialize',
);
const deleteCommentInitialize = createAction(
  'component/commentSection/deleteCommentInitialize',
);
const setIsResponsePanelOpen = createAction(
  'component/commentSection/setIsResponsePanelOpen',
);
const setOpenCommentId = createAction(
  'component/commentSection/setOpenCommentId',
);
const setIsFetchingComments = createAction(
  'component/commentSection/setIsFetchingComments',
);
const setIsOnBehalfOf = createAction(
  'component/commentSection/setIsOnBehalfOf',
);
const setOnBehalfOf = createAction('component/commentSection/setOnBehalfOf');
const addIdToCommentIdsBeingUpdated = createAction(
  'component/commentSection/addIdToCommentIdsBeingUpdated',
);
const removeIdFromCommentIdsBeingUpdated = createAction(
  'component/commentSection/removeIdFromCommentIdsBeingUpdated',
);
const setCommentReplyModel = createAction(
  'component/commentSection/setCommentReplyModel',
);
const setIsCommentReplyModelUpdating = createAction(
  'component/commentSection/setIsCommentReplyModelUpdating',
);

const setCurrentCommentsPage = createAction(
  'component/commentSection/setCurrentCommentsPage',
);
const setPaginatedComments = createAction(
  'component/commentSection/setPaginatedComments',
);
const setTotalCommentsPages = createAction(
  'component/commentSection/setTotalCommentsPages',
);
const setTotalComments = createAction(
  'component/commentSection/setTotalComments',
);

const fetchCommentsForHearing = createAction(
  'component/commentSection/fetchCommentsForHearing',
);
const updateCurrentCommentsPage = createAction(
  'component/commentSection/updateCurrentCommentsPage',
);

const resetPagedComments = createAction(
  'component/commentSection/initializePaginatedComments',
);
const setActiveHearing = createAction(
  'component/commentSection/setActiveHearing',
);
const updateActiveHearing = createAction(
  'component/commentSection/updateActiveHearing',
);

const commentsPerPage = process.env.REACT_APP_COMMENTS_PER_PAGE;

const initialState = {
  contentDiffs: [],
  isUpdatingComment: false,
  isFetchingComments: false,
  isResponsePanelOpen: false,
  openCommentId: null,
  isOnBehalfOf: false,
  onBehalfOf: null,
  commentIdsBeingUpdated: [],
  commentReplyModel: null,
  isCommentReplyModelUpdating: false,

  activeHearing: null,
  currentPage: 1,
  pageSize: commentsPerPage === undefined ? 6 : parseInt(commentsPerPage),
  totalPages: 1,
  totalComments: {},
  paginatedComments: {},
};

// --------- Reducers ---------
const commentSectionReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setIsUpdatingComment, (state, action) => {
      state.isUpdatingComment = action.payload;
    })
    .addCase(updateContentDiffInternal, (state, action) => {
      state.contentDiffs = action.payload;
    })
    .addCase(resetContentDiff, (state) => {
      state.contentDiffs = [];
    })
    .addCase(setIsResponsePanelOpen, (state, action) => {
      state.isResponsePanelOpen = action.payload;
    })
    .addCase(setOpenCommentId, (state, action) => {
      state.openCommentId = action.payload;
    })
    .addCase(getAllCommentsForHearingInitialize, (state) => {
      state.isFetchingComments = true;
    })
    .addCase(getAllCommentsForHearingError, (state) => {
      state.isFetchingComments = false;
    })
    .addCase(getAllCommentsForHearingSuccess, (state, action) => {
      const comments = getRequestData(action);
      const initAction = getInitActionPayload(action);
      const hearingId = initAction.hearingId;
      state.totalComments[hearingId] = comments.length;
      state.isFetchingComments = false;
    })
    .addCase(getPaginatedCommentsForHearingInitialize, (state) => {
      state.isFetchingComments = true;
    })
    .addCase(getPaginatedCommentsForHearingError, (state) => {
      state.isFetchingComments = false;
    })
    .addCase(getPaginatedCommentsForHearingSuccess, (state) => {
      state.isFetchingComments = false;
    })
    .addCase(setIsFetchingComments, (state, action) => {
      state.isFetchingComments = action.payload;
    })
    .addCase(setOnBehalfOf, (state, action) => {
      state.onBehalfOf = action.payload;
    })
    .addCase(setIsOnBehalfOf, (state, action) => {
      state.isOnBehalfOf = action.payload;
      state.onBehalfOf = null;
    })
    .addCase(setCommentReplyModel, (state, action) => {
      state.commentReplyModel = action.payload;
    })
    .addCase(setIsCommentReplyModelUpdating, (state, action) => {
      state.isCommentReplyModelUpdating = action.payload;
    })
    .addCase(addIdToCommentIdsBeingUpdated, (state, action) => {
      state.commentIdsBeingUpdated.push(action.payload);
    })
    .addCase(removeIdFromCommentIdsBeingUpdated, (state, action) => {
      const newCommentIdsBeingUpdated = state.commentIdsBeingUpdated.filter(
        (commentId) => commentId !== action.payload,
      );

      return Object.assign({}, state, {
        commentIdsBeingUpdated: newCommentIdsBeingUpdated,
      });
    })
    .addCase(resetPagedComments, (state) => {
      state.currentPage = 1;
      state.totalPages = 1;
    })
    .addCase(setCurrentCommentsPage, (state, action) => {
      state.currentPage = action.payload;
    })
    .addCase(setTotalCommentsPages, (state, action) => {
      state.totalPages = action.payload;
    })
    .addCase(setPaginatedComments, (state, action) => {
      const hearingId = action.payload.hearingId;
      const pageIndex = action.payload.pageIndex;
      const commentIds = action.payload.commentIds;
      state.paginatedComments[`${hearingId}.${pageIndex}`] = commentIds;
    })
    .addCase(setActiveHearing, (state, action) => {
      state.activeHearing = action.payload;
    })
    .addCase(setTotalComments, (state, action) => {
      const hearingId = action.payload.hearingId;
      state.totalComments[hearingId] = action.payload.totalComments;
    });
});

// --------- Sagas ----------
export function* commentSectionSaga() {
  yield all([
    fork(updateActiveHearingSaga),
    fork(updateCurrentCommentsPageSaga),
    fork(fetchCommentsForHearingSaga),
    fork(updateContentDiffSaga),
    fork(createOrUpdateCommentContentSaga),
    fork(deleteCommentSaga),
    fork(updateCommentStatusSaga),
    fork(createOrUpdateCommentReplySaga),
  ]);
}
function* updateActiveHearingSaga() {
  yield takeEvery(updateActiveHearing.type, function* (action) {
    yield put(setActiveHearing(action.payload));
    yield put(resetPagedComments());
    yield put(fetchCommentsForHearing(action.payload));
  });
}

function* updateCurrentCommentsPageSaga() {
  yield takeEvery(updateCurrentCommentsPage.type, function* (action) {
    yield put(setCurrentCommentsPage(action.payload.pageIndex));
    yield put(fetchCommentsForHearing(action.payload.hearingId));
  });
}

function* fetchCommentsForHearingSaga() {
  yield takeLatest(fetchCommentsForHearing.type, function* (action) {
    const usingPagination = yield select(shouldPaginateCommentsSelector);
    const hearingId = action.payload;
    const includes = buildIncludesString(
      commentIncludes.contents,
      commentIncludes.contentTypes,
      commentIncludes.commentDeclineInfo,
    );

    if (!usingPagination) {
      return yield put(
        getAllCommentsForHearingInitialize({ hearingId, includes }),
      );
    }

    const pageIndex = yield select(currentPageCommentsSelector);
    const pageSize = yield select(pageSizeCommentsSelector);

    const actionResult = yield callAndAwaitApi(
      getPaginatedCommentsForHearingInitialize({
        hearingId: hearingId,
        pageIndex: pageIndex,
        pageSize: pageSize,
        includes: includes,
      }),
    );

    if (actionResult.success) {
      const meta = getRequestMetaData(actionResult.success);
      const totalPages = meta.Pagination.totalPages;
      const totalComments = meta.Pagination.totalItems;
      const pageIndex = meta.Pagination.pageIndex;
      const comments = getRequestData(actionResult.success);
      const commentIds = comments.map((c) => getEntityId(c));

      yield put(setTotalCommentsPages(totalPages));
      yield put(setTotalComments({ totalComments, hearingId }));
      yield put(
        setPaginatedComments({
          hearingId: hearingId,
          pageIndex: pageIndex,
          commentIds: commentIds,
        }),
      );
    } else {
      yield put(parseApiError(actionResult.error.payload));
    }
  });
}

function* deleteCommentSaga() {
  yield takeLatest(deleteCommentInitialize.type, function* (action) {
    const { hearingId, commentId } = action.payload;

    yield put(setIsFetchingComments(true));

    const updateCommentInitAction = yield put(
      updateCommentInitialize({ hearingId, commentId }),
    );

    const updateCommentResult = yield awaitApiCall(updateCommentInitAction);

    if (updateCommentResult.error) {
      yield put(parseApiError(updateCommentResult.error.payload));
    }
    yield put(setIsFetchingComments(false));
  });
}

function* updateCommentStatusSaga() {
  yield takeEvery(updateCommentStatusInitialize.type, function* (action) {
    const { hearingId, commentId, status, declineReason } = action.payload;

    if (!hearingId || !commentId || !status) {
      return;
    }

    if (
      status === COMMENTSTATUS_NOTAPPROVED &&
      (declineReason === null || declineReason.trim().length === 0)
    ) {
      return;
    }

    yield put(addIdToCommentIdsBeingUpdated(commentId));

    const currentComment = yield select(
      commentDataByCommentIdSelector(hearingId, commentId),
    );

    const content =
      getAttr(currentComment && currentComment.textContent, 'textContent') ||
      '';
    const onBehalfOf = getAttr(
      currentComment && currentComment.comment,
      'onBehalfOf',
    );

    let dto = {
      commentStatus: status,
      content,
      fileOperations: [],
    };

    if (onBehalfOf) {
      dto.onBehalfOf = onBehalfOf;
    }

    if (status === COMMENTSTATUS_NOTAPPROVED) {
      dto.commentDeclineReason = declineReason;
    }

    const initAction = yield put(
      updateCommentContentInitialize({ hearingId, commentId, dto }),
    );

    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.success) {
      yield put(fetchCommentsForHearing(hearingId));
    } else {
      yield put(parseApiError(actionResult.error.payload));
    }

    yield put(removeIdFromCommentIdsBeingUpdated(commentId));
  });
}

function* createOrUpdateCommentReplySaga() {
  yield takeLatest(createOrUpdateCommentReplyInitialize.type, function* (
    action,
  ) {
    const { commentId, hearingId, parrentCommentId, text } = action.payload;

    if (!(hearingId && parrentCommentId)) {
      return;
    }

    yield put(setIsCommentReplyModelUpdating(true));

    const commentType = COMMENTTYPE_HEARING_RESPONSE_REPLY;
    const commentStatus = COMMENTSTATUS_NONE;

    const dto = {
      commentType,
      commentStatus,
      content: text,
      fileOperations: [],
      commentParrentId: parrentCommentId,
    };

    let initAction;
    if (commentId) {
      initAction = yield put(
        updateCommentContentInitialize({ hearingId, commentId, dto }),
      );
    } else {
      initAction = yield put(
        createCommentContentInitialize({ hearingId, dto }),
      );
    }
    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.success) {
      yield put(fetchCommentsForHearing(hearingId));
      yield put(setCommentReplyModel(null));
    } else {
      yield put(parseApiError(actionResult.error.payload));
    }
    yield put(setIsCommentReplyModelUpdating(false));
  });
}

function* createOrUpdateCommentContentSaga() {
  yield takeLatest(createOrUpdateCommentContentInitialize.type, function* (
    action,
  ) {
    const {
      hearingId,
      commentId,
      hearingStatus,
      isCurrentUserOwnerOfHearing,
    } = action.payload;

    // Set commentType based on hearingStatus
    let commentType = null;
    let commentStatus = null;
    if (
      hearingStatus === HEARINGSTATUS_ACTIVE ||
      (hearingStatus === HEARINGSTATUS_AWAITING_CONCLUSION &&
        isCurrentUserOwnerOfHearing)
    ) {
      commentType = COMMENTTYPE_HEARING_RESPONSE;
      commentStatus = COMMENTSTATUS_AWAITING_APPROVAL;
    } else if (
      hearingStatus === HEARINGSTATUS_DRAFT ||
      hearingStatus === HEARINGSTATUS_AWAITING_STARTDATE
    ) {
      commentType = COMMENTTYPE_HEARING_REVIEW;
      commentStatus = COMMENTSTATUS_NONE;
    }

    // HearingId and CommentType must have values!
    if (!(hearingId && commentType)) {
      return;
    }

    yield put(setIsUpdatingComment(true));

    const contentDiffs = yield select(contentDiffSelector);
    const currentComment = yield select(
      commentDataByCommentIdSelector(hearingId, commentId),
    );

    const isOnBehalfOf = yield select(isOnBehalfOfSelector);
    const onBehalfOf = yield select(onBehalfOfSelector);

    const currentText =
      getAttr(currentComment && currentComment.textContent, 'textContent') ||
      '';

    let dto = {
      commentType,
      commentStatus,
      content: currentText,
      fileOperations: [],
    };

    if (commentType === COMMENTTYPE_HEARING_RESPONSE && isOnBehalfOf) {
      dto.onBehalfOf = onBehalfOf;
    }
    const contentIdsToDelete = [];

    for (let diff of contentDiffs) {
      if (diff.contentType === CONTENTTYPE_TEXT) {
        dto.content = diff.content || currentText;
      } else if (diff.contentType === CONTENTTYPE_FILE) {
        const currentFileContent =
          currentComment &&
          currentComment.fileContent &&
          currentComment.fileContent.find(
            (file) => getAttr(file, 'fileName') === diff.content.fileName,
          );

        let fileOperationDelete = null;
        let fileOperationAdd = null;

        const isUpdateOperation = !diff.isDeleteOperation;

        if (!currentFileContent && diff.isDeleteOperation) {
          continue;
        }

        if (currentFileContent) {
          contentIdsToDelete.push(getEntityId(currentFileContent));
          fileOperationDelete = {
            file: null,
            contentId: getEntityId(currentFileContent),
            operation: FILEOPERATION_DELETE,
          };
        }

        if (!currentFileContent || isUpdateOperation) {
          const file = yield call(
            convertDataUrlToFile,
            diff.content.fileContentUrl,
            diff.content.fileName,
            diff.content.fileType,
          );
          fileOperationAdd = {
            file: file,
            operation: FILEOPERATION_ADD,
          };
        }

        fileOperationDelete && dto.fileOperations.push(fileOperationDelete);
        fileOperationAdd && dto.fileOperations.push(fileOperationAdd);
      }
    }

    let initAction;

    // Updating an existing comment
    if (commentId) {
      initAction = yield put(
        updateCommentContentInitialize({ hearingId, commentId, dto }),
      );
    } else {
      initAction = yield put(
        createCommentContentInitialize({ hearingId, dto }),
      );
    }

    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.success) {
      if (commentType === COMMENTTYPE_HEARING_RESPONSE) {
        yield put(
          setGlobalMessage({ mode: globalMessageModes.commentReceived }),
        );
      }
      yield put(setIsResponsePanelOpen(false));
      yield put(setOpenCommentId(null));
      yield put(resetContentDiff());
      yield put(fetchCommentsForHearing(hearingId));
      yield put(removeContentsFromStore(contentIdsToDelete));
    } else {
      yield put(parseApiError(actionResult.error.payload));
    }

    yield put(setIsUpdatingComment(false));
  });
}

function* updateContentDiffSaga() {
  yield takeLatest(updateContentDiff.type, function* (action) {
    let diffs = JSON.parse(JSON.stringify(yield select(contentDiffSelector)));
    let newContent = action.payload;

    mapFileContentDiff(newContent);

    let { commentId, contentType, content, allowMultiple } = newContent;

    if (!Array.isArray(content)) {
      content = [content];
    }

    content.forEach((ele) => {
      const indexOfContent = diffs.findIndex(
        (c) =>
          c.commentId === commentId &&
          c.contentType === contentType &&
          (!allowMultiple || c.content.fileName === ele.fileName),
      );
      const contentToPush = Object.assign({}, newContent, { content: ele });
      indexOfContent === -1
        ? diffs.push(contentToPush)
        : (diffs[indexOfContent] = contentToPush);
    });
    yield put(updateContentDiffInternal(diffs));
  });
}

// --------- Selectors ---------
const commentSectionState = (state) =>
  state.component[COMPONENT_REDUCER.COMMENT_SECTION];

const isUpdatingCommentSelector = createSelector(
  commentSectionState,
  (state) => state.isUpdatingComment,
);

const contentDiffSelector = createSelector(
  commentSectionState,
  (state) => state.contentDiffs,
);

const isFetchingCommentsSelector = createSelector(
  commentSectionState,
  (state) => state.isFetchingComments,
);

const isResponsePanelOpenSelector = createSelector(
  commentSectionState,
  (state) => state.isResponsePanelOpen,
);

const openCommentIdSelector = createSelector(
  commentSectionState,
  (state) => state.openCommentId,
);

const onBehalfOfSelector = createSelector(
  commentSectionState,
  (state) => state.onBehalfOf,
);

const isOnBehalfOfSelector = createSelector(
  commentSectionState,
  (state) => state.isOnBehalfOf,
);

const activeHearingSelector = createSelector(
  commentSectionState,
  (state) => state.activeHearing,
);

const currentPageCommentsSelector = createSelector(
  commentSectionState,
  (state) => state.currentPage,
);

const pageSizeCommentsSelector = createSelector(
  commentSectionState,
  (state) => state.pageSize,
);

const totalPagesCommentsSelector = createSelector(
  commentSectionState,
  (state) => state.totalPages,
);

const totalCommentsSelector = createSelector(
  [commentSectionState, activeHearingSelector],
  (state, activeHearing) => state.totalComments[activeHearing] ?? 0,
);

const paginatedCommentsSelector = createSelector(
  [commentSectionState, currentPageCommentsSelector, activeHearingSelector],
  (state, currentPage, activeHearing) =>
    state.paginatedComments[`${activeHearing}.${currentPage}`] ?? [],
);

const areCommentsReadyForPageSelector = createSelector(
  [shouldPaginateCommentsSelector, paginatedCommentsSelector],
  (usingPagination, commentIds) =>
    usingPagination && commentIds !== undefined && commentIds.length > 0,
);

const commentReplyModelSelector = (commentId) =>
  createSelector(commentSectionState, (state) =>
    state.commentReplyModel &&
    state.commentReplyModel.parrentCommentId === commentId
      ? state.commentReplyModel
      : null,
  );

const isCommentReplyModelUpdatingSelector = createSelector(
  commentSectionState,
  (state) => state.isCommentReplyModelUpdating,
);

const isCommentWithIdBeingUpdatedSelector = (commentId) =>
  createSelector(commentSectionState, (state) =>
    state.commentIdsBeingUpdated.includes(commentId),
  );

const commentDataSelector = (hearingId, commentType) => (state) => {
  const baseApiUrl = baseApiUrlSelector(state);
  const users = allUsersSelector(state);
  const companies = allCompaniesSelector(state);
  const commentTypeEntity = commentTypeByTypeSelector(commentType)(state);
  const comments = allNotDeletedCommentsWithTypeByHearingIdSelector(
    hearingId,
    getEntityId(commentTypeEntity),
  )(state);
  const contents = contentByHearingIdSelector(hearingId)(state);
  const textContentType = contentTypeByTypeSelector(CONTENTTYPE_TEXT)(state);
  const fileContentType = contentTypeByTypeSelector(CONTENTTYPE_FILE)(state);

  let commentChildren = [];
  if (commentType === COMMENTTYPE_HEARING_RESPONSE) {
    const commentChildrenCommentType = commentTypeByTypeSelector(
      COMMENTTYPE_HEARING_RESPONSE_REPLY,
    )(state);
    commentChildren = allNotDeletedCommentsWithTypeByHearingIdSelector(
      hearingId,
      getEntityId(commentChildrenCommentType),
    )(state);
  }

  return comments.map((comment) => {
    const commentOwner = users.find(
      (user) => getRelId(comment, 'user') === getEntityId(user),
    );

    const company = companies.find(
      (company) => getRelId(commentOwner, 'company') === getEntityId(company),
    );

    const commentContent = contents.filter(
      (content) => getRelId(content, 'comment') === getEntityId(comment),
    );

    const [textContent] = commentContent.filter(
      (content) =>
        getRelId(content, 'contentType') === getEntityId(textContentType),
    );
    const fileContent = commentContent
      .filter(
        (content) =>
          getRelId(content, 'contentType') === getEntityId(fileContentType),
      )
      .map(mapFileContentWithUrlToDownload(baseApiUrl));

    const [commentChild] = commentChildren.filter(
      (child) => getRelId(child, 'commentParrent') === getEntityId(comment),
    );
    const [childTextContent] = contents.filter(
      (content) =>
        getRelId(content, 'comment') === getEntityId(commentChild) &&
        getRelId(content, 'contentType') === getEntityId(textContentType),
    );

    return {
      id: getEntityId(comment),
      comment,
      textContent,
      childTextContent,
      childCommentId: getEntityId(commentChild),
      fileContent,
      commentOwner,
      company,
      hearingId,
    };
  });
};

const commentDataByCommentIdSelector = (hearingId, commentId) => (state) => {
  const commentType = commentTypeByCommentIdSelector(commentId)(state);
  const commentData = commentDataSelector(
    hearingId,
    getAttr(commentType, 'commentType'),
  )(state);
  const currentComment = commentData.find(
    (comment) => comment.id === commentId,
  );
  return currentComment;
};

const commentModelSelector = (hearingId, commentId) =>
  createSelector(
    [commentDataByCommentIdSelector(hearingId, commentId), contentDiffSelector],
    (commentData, contentDiffs) => {
      // Comment does not already exist i.e. it is a new comment
      if (!commentData) {
        const fileDiffs = contentDiffs.filter(
          (diff) =>
            diff.contentType === CONTENTTYPE_FILE && !diff.isDeleteOperation,
        );
        const textDiff = contentDiffs.find(
          (diff) => diff.contentType === CONTENTTYPE_TEXT,
        );

        return {
          id: null,
          textContent: textDiff && createNewTextContent(textDiff.content),
          fileContent: fileDiffs
            ? fileDiffs.map((diff) => createNewFileContent(diff.content))
            : [],
        };
      }

      // Comment already exist and must be overwritten with the diffs
      let currentCommentData = JSON.parse(JSON.stringify(commentData));

      contentDiffs.forEach((diff) => {
        if (diff.contentType === CONTENTTYPE_TEXT) {
          if (currentCommentData.textContent) {
            currentCommentData.textContent.attributes.textContent =
              diff.content;
          } else {
            currentCommentData.textContent = createNewTextContent(diff.content);
          }
        }
        if (diff.contentType === CONTENTTYPE_FILE) {
          const indexOfExistingContent = currentCommentData.fileContent.findIndex(
            (c) => getAttr(c, 'fileName') === diff.content.fileName,
          );
          if (indexOfExistingContent !== -1) {
            if (!diff.content.isDeleteOperation) {
              let newFileContent =
                currentCommentData.fileContent[indexOfExistingContent];

              newFileContent.attributes.urlToDownload =
                diff.content.fileContentUrl;
              newFileContent.attributes.fileName = diff.content.fileName;
              newFileContent.attributes.fileContentType = diff.content.fileType;

              currentCommentData.fileContent[
                indexOfExistingContent
              ] = newFileContent;
            } else {
              currentCommentData.fileContent = currentCommentData.fileContent.filter(
                (_, index) => index !== indexOfExistingContent,
              );
            }
          } else {
            if (!diff.content.isDeleteOperation) {
              const newFileContent = createNewFileContent(diff.content);
              currentCommentData.fileContent.push(newFileContent);
            }
          }
        }
      });
      return currentCommentData;
    },
  );

export {
  areCommentsReadyForPageSelector,
  commentDataSelector,
  commentModelSelector,
  commentReplyModelSelector,
  createOrUpdateCommentContentInitialize,
  createOrUpdateCommentReplyInitialize,
  currentPageCommentsSelector,
  deleteCommentInitialize,
  isCommentReplyModelUpdatingSelector,
  isCommentWithIdBeingUpdatedSelector,
  isFetchingCommentsSelector,
  isOnBehalfOfSelector,
  isResponsePanelOpenSelector,
  isUpdatingCommentSelector,
  onBehalfOfSelector,
  openCommentIdSelector,
  paginatedCommentsSelector,
  resetContentDiff,
  setCommentReplyModel,
  setIsOnBehalfOf,
  setIsResponsePanelOpen,
  setOnBehalfOf,
  setOpenCommentId,
  totalCommentsSelector,
  totalPagesCommentsSelector,
  updateActiveHearing,
  updateCommentStatusInitialize,
  updateContentDiff,
  updateCurrentCommentsPage,
};

export default commentSectionReducer;

const createNewTextContent = (value) => {
  return {
    attributes: {
      textContent: value,
    },
    type: CONTENT_TYPE,
  };
};

const createNewFileContent = (value) => {
  return {
    attributes: {
      fileName: value.fileName,
      fileContentType: value.fileType,
      urlToDownload: value.fileContentUrl,
    },
    type: CONTENT_TYPE,
  };
};
